import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Typography, Divider, Chip, Avatar, IconButton, Button, Tooltip, List,
  ListItem, ListItemAvatar, ListItemText
} from '@mui/material';
import {
  Group, Assignment, AccessTime, Warning, ChevronLeft, ChevronRight, OpenInNew, HowToReg,
  GppMaybe, CheckCircle, Visibility
} from '@mui/icons-material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import 'dayjs/locale/es';
import { reportsAPI, projectsAPI, activitiesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import DevBadge from '../../components/common/DevBadge';

dayjs.extend(isoWeek);
dayjs.locale('es');

const CATEGORY_COLORS = {
  'EJECUCIÓN': '#2979FF', 'PLANEACIÓN': '#00C853', 'REVISIÓN': '#FF6D00',
  'ANÁLISIS': '#AA00FF', 'BLOQUEO': '#D50000', 'SIN ASIGNACIÓN': '#757575'
};

function StatCard({ icon, label, value, color, sub }) {
  return (
    <Card sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Avatar sx={{ bgcolor: `${color}20`, color, width: 48, height: 48 }}>{icon}</Avatar>
      <Box>
        <Typography variant="h5" fontWeight={700}>{value}</Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        {sub && <Typography variant="caption" color="text.disabled">{sub}</Typography>}
      </Box>
    </Card>
  );
}

export default function TeamLeadDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => dayjs().startOf('isoWeek'));
  const [disputes, setDisputes] = useState({ pending: [], resolved: [] });
  const [disputeTab, setDisputeTab] = useState('pending');

  const weekEnd = weekStart.endOf('isoWeek');
  const isCurrentWeek = weekStart.isSame(dayjs().startOf('isoWeek'), 'day');
  const weekNum = weekStart.isoWeek();

  const loadData = useCallback(async (ws) => {
    setLoading(true);
    const we = ws.endOf('isoWeek');
    try {
      const [rpt, prj] = await Promise.all([
        reportsAPI.getDashboard({ startDate: ws.format('YYYY-MM-DD'), endDate: we.format('YYYY-MM-DD') }),
        projectsAPI.getAll()
      ]);
      setData(rpt.data);
      setProjects(prj.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDisputes = useCallback(async () => {
    if (!user?._id) return;
    try {
      const [pendingRes, resolvedRes] = await Promise.all([
        activitiesAPI.getAll({ disputeRequestedBy: user._id, disputeStatus: 'pending', limit: 10 }),
        activitiesAPI.getAll({ disputeRequestedBy: user._id, disputeStatus: 'resolved', limit: 10 })
      ]);
      setDisputes({
        pending: pendingRes.data.logs || [],
        resolved: resolvedRes.data.logs || []
      });
    } catch (err) {
      console.error(err);
    }
  }, [user?._id]);

  useEffect(() => { loadData(weekStart); }, [weekStart, loadData]);
  useEffect(() => { loadDisputes(); }, [loadDisputes]);

  const prevWeek = () => setWeekStart(ws => ws.subtract(1, 'week'));
  const nextWeek = () => setWeekStart(ws => ws.add(1, 'week'));
  const goCurrentWeek = () => setWeekStart(dayjs().startOf('isoWeek'));

  const formatWeekRange = (ws, we) => {
    if (ws.month() === we.month())
      return `${ws.format('D')} – ${we.format('D')} de ${we.format('MMMM')} de ${we.format('YYYY')}`;
    if (ws.year() === we.year())
      return `${ws.format('D')} de ${ws.format('MMM')} – ${we.format('D')} de ${we.format('MMMM')} de ${we.format('YYYY')}`;
    return `${ws.format('D MMM YYYY')} – ${we.format('D MMM YYYY')}`;
  };

  if (loading) return <LoadingSpinner />;

  const byCat = data?.hoursByCategory || [];
  const byProject = data?.hoursByProject || [];

  const donutOptions = {
    chart: { type: 'pie', height: 280, backgroundColor: 'transparent' },
    title: { text: null },
    tooltip: { pointFormat: '<b>{point.y:.1f}h</b> ({point.percentage:.0f}%)' },
    plotOptions: { pie: { innerSize: '55%', dataLabels: { enabled: true, format: '{point.name}<br/><b>{point.y:.1f}h</b> ({point.percentage:.0f}%)', style: { fontSize: '11px', fontWeight: 'normal', textOutline: 'none' } } } },
    credits: { enabled: false },
    series: [{
      name: 'Horas',
      data: byCat.map(c => ({ name: c._id, y: c.total, color: CATEGORY_COLORS[c._id] || '#90A4AE' }))
    }]
  };

  const barOptions = {
    chart: { type: 'bar', height: 280, backgroundColor: 'transparent' },
    title: { text: null },
    xAxis: { categories: byProject.map(p => p.projectName || p._id), gridLineWidth: 0 },
    yAxis: { title: { text: 'Horas' }, gridLineDashStyle: 'dot' },
    tooltip: { valueSuffix: 'h' },
    credits: { enabled: false },
    legend: { enabled: false },
    plotOptions: { bar: { dataLabels: { enabled: true, format: '{point.y:.1f}h', style: { fontSize: '10px', fontWeight: 'normal', textOutline: 'none' } } } },
    series: [{ name: 'Horas', data: byProject.map(p => parseFloat((p.totalHours || 0).toFixed(1))), color: 'rgb(132, 0, 255)' }]
  };

  const missingMembers = (data?.totalMembers ?? 0) - (data?.membersWithHours ?? 0);

  return (
    <Box>
      <DevBadge id="page:teamlead-dashboard" />
      <Typography variant="h5" fontWeight={700} mb={0.5}>
        Bienvenido{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Resumen de actividad de tu equipo
      </Typography>

      {/* Week navigation header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Indicadores — semana {weekNum}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatWeekRange(weekStart, weekEnd)}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <IconButton size="small" onClick={prevWeek}><ChevronLeft /></IconButton>
          <Typography variant="body2" sx={{ minWidth: 30, textAlign: 'center' }}>S{weekNum}</Typography>
          <IconButton size="small" onClick={nextWeek} disabled={isCurrentWeek}><ChevronRight /></IconButton>
          {!isCurrentWeek && (
            <Button size="small" variant="outlined" sx={{ ml: 1, textTransform: 'none', fontSize: 12 }} onClick={goCurrentWeek}>
              Hoy
            </Button>
          )}
        </Box>
      </Box>

      {/* Stat cards — 5-column responsive CSS grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
        <StatCard icon={<Assignment />} label="Proyectos activos" value={data?.activeProjects ?? '—'} color="#2979FF" />
        <StatCard icon={<Group />} label="Miembros de equipo" value={data?.totalMembers ?? '—'} color="#00C853" />
        <StatCard
          icon={<HowToReg />}
          label="Miembros con horas"
          value={data != null ? `${data.membersWithHours} / ${data.totalMembers}` : '—'}
          color="#AA00FF"
          sub={data != null ? (missingMembers > 0 ? `${missingMembers} sin registrar` : 'Todos registraron') : undefined}
        />
        <StatCard
          icon={<AccessTime />}
          label="Horas semana"
          value={data?.weekHours != null ? `${data.weekHours.toFixed(0)}h` : '—'}
          color="#FF6D00"
        />
        <StatCard
          icon={<Warning />}
          label={`Bloqueos semana ${weekNum}`}
          value={data?.weekBlockages ?? '—'}
          color="#D50000"
        />
      </Box>

      {/* Charts */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
        <Card sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={1}>Horas por Categoría</Typography>
          <Divider sx={{ mb: 2 }} />
          {byCat.length > 0
            ? <HighchartsReact highcharts={Highcharts} options={donutOptions} />
            : <Typography variant="body2" color="text.secondary" py={4} textAlign="center">Sin datos para esta semana</Typography>}
        </Card>
        <Card sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={1}>Horas por Proyecto</Typography>
          <Divider sx={{ mb: 2 }} />
          {byProject.length > 0
            ? <HighchartsReact highcharts={Highcharts} options={barOptions} />
            : <Typography variant="body2" color="text.secondary" py={4} textAlign="center">Sin datos para esta semana</Typography>}
        </Card>
      </Box>

      {/* Mis Refutaciones */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" fontWeight={600}>Mis Refutaciones</Typography>
            {disputes.pending.length > 0 && (
              <Chip label={`${disputes.pending.length} sin respuesta`} size="small" color="error" />
            )}
            {disputes.resolved.length > 0 && (
              <Chip label={`${disputes.resolved.length} respondidas`} size="small" color="success" variant="outlined" />
            )}
          </Box>
          <Box display="flex" gap={0.5}>
            <Chip
              label="Sin respuesta"
              size="small"
              icon={<GppMaybe />}
              onClick={() => setDisputeTab('pending')}
              color={disputeTab === 'pending' ? 'error' : 'default'}
              variant={disputeTab === 'pending' ? 'filled' : 'outlined'}
              clickable
            />
            <Chip
              label="Respondidas"
              size="small"
              icon={<CheckCircle />}
              onClick={() => setDisputeTab('resolved')}
              color={disputeTab === 'resolved' ? 'success' : 'default'}
              variant={disputeTab === 'resolved' ? 'filled' : 'outlined'}
              clickable
            />
          </Box>
        </Box>
        <List dense disablePadding>
          {(disputeTab === 'pending' ? disputes.pending : disputes.resolved).slice(0, 5).map((act, idx, arr) => {
            const proj = projects.find(p => p._id === act.projectId?.toString() || p._id === act.projectId);
            const projName = proj?.name || 'Proyecto';
            return (
              <React.Fragment key={act._id}>
                <ListItem
                  sx={{ px: 2, py: 1.5 }}
                  secondaryAction={
                    <Tooltip title="Ver en actividades">
                      <IconButton size="small" onClick={() => navigate(`/teamlead/activities?projectId=${act.projectId}`)}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemAvatar>
                    <Avatar sx={{
                      width: 34, height: 34,
                      bgcolor: disputeTab === 'pending' ? '#D5000020' : '#00C85320',
                      color: disputeTab === 'pending' ? '#D50000' : '#00C853'
                    }}>
                      {disputeTab === 'pending' ? <GppMaybe fontSize="small" /> : <CheckCircle fontSize="small" />}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body2" noWrap sx={{ maxWidth: 380 }}>
                        {act.description}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {projName} · {act.userEmail} · refutada el {dayjs(act.dispute?.requestedAt).format('DD/MM/YYYY')}
                      </Typography>
                    }
                  />
                </ListItem>
                {idx < arr.length - 1 && <Divider component="li" />}
              </React.Fragment>
            );
          })}
          {(disputeTab === 'pending' ? disputes.pending : disputes.resolved).length === 0 && (
            <ListItem sx={{ px: 2 }}>
              <ListItemText secondary={
                disputeTab === 'pending'
                  ? 'No hay refutaciones pendientes de respuesta.'
                  : 'No hay refutaciones respondidas aún.'
              } />
            </ListItem>
          )}
        </List>
        {(disputeTab === 'pending' ? disputes.pending : disputes.resolved).length > 5 && (
          <Box px={2} pb={1.5}>
            <Button size="small" variant="text" onClick={() => navigate('/teamlead/activities')} sx={{ textTransform: 'none', fontSize: 12 }}>
              Ver todas →
            </Button>
          </Box>
        )}
      </Card>

      {/* Mis Proyectos */}
      <Card>
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <Typography variant="subtitle1" fontWeight={600}>Mis Proyectos</Typography>
        </Box>
        <Box>
          {projects.slice(0, 5).map((p, idx) => (
            <Box key={p._id}>
              <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1.5}>
                <Box>
                  <Typography variant="body1" fontWeight={500}>{p.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{p.clientId?.name || 'Sin cliente'}</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" justifyContent="flex-end">
                  <Chip label={`${p.members?.length ?? 0} miembros`} size="small" variant="outlined" />
                  <Chip
                    label={p.status === 'active' ? 'Activo' : p.status === 'completed' ? 'Terminado' : 'En pausa'}
                    size="small"
                    color={p.status === 'active' ? 'success' : p.status === 'completed' ? 'default' : 'warning'}
                  />
                  <Tooltip title="Ver actividades del proyecto">
                    <IconButton size="small" onClick={() => navigate(`/teamlead/activities?projectId=${p._id}`)}>
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              {idx < Math.min(projects.length, 5) - 1 && <Divider />}
            </Box>
          ))}
          {projects.length === 0 && (
            <Box p={3} textAlign="center">
              <Typography variant="body2" color="text.secondary">No tienes proyectos asignados aún.</Typography>
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  );
}
